import { ComponentType } from "react"
import Dashboard from "../pages/Dashboard"
import Login from "../pages/Login"
import { SidebarGroup, SidebarLink, SidebarSection, WrapperType } from "../types/routes"
import NotFound from "../pages/NotFound"
import Challenges from "../pages/Challenges"
import Challenge from "../pages/Challenge"
import News from "../pages/News"
import Users from "../pages/Users"
import User from "../pages/User"
import RedeemCodes from "../pages/RedeemCodes"

// USER BASED REDIRECTS
export const authRedirectPath = "/login"
export const privateRedirectPath = "/"

// TYPES
type SidebarSectionId = keyof typeof sidebarSectionsConfig
type SidebarGroupId = keyof typeof sidebarGroupsConfig
type SidebarOptions = {
  label: string
  section: SidebarSectionId
  group?: SidebarGroupId
  icon?: ComponentType
}
type SectionsConfig = {
  [key in SidebarSectionId]: {
    label?: string
  }
}
type GroupsConfig = {
  [key in SidebarGroupId]: {
    label: string
    icon?: ComponentType
  }
}
type Route = {
  path: string
  component: ComponentType
  wrapper: WrapperType
  sidebarOptions?: SidebarOptions
}

// CONFIGS
export const routes: Route[] = [
  {
    path: "/",
    component: Dashboard,
    wrapper: "private",
    sidebarOptions: {
      label: "Dashboard",
      section: "main"
    }
  },
  {
    path: "/login",
    component: Login,
    wrapper: "auth"
  },
  {
    path: "/challenges",
    component: Challenges,
    wrapper: "private",
    sidebarOptions: {
      label: "Challenges",
      section: "pages"
    }
  },
  {
    path: "/challenge/:id",
    component: Challenge,
    wrapper: "private"
  },
  {
    path: "/news",
    component: News,
    wrapper: "private",
    sidebarOptions: {
      label: "News",
      section: "pages"
    }
  },
  {
    path: "/users",
    component: Users,
    wrapper: "private",
    sidebarOptions: {
      label: "Users",
      section: "pages"
    }
  },
  {
    path: "/user/:id",
    component: User,
    wrapper: "private"
  },
  {
    path: "/redeem-codes",
    component: RedeemCodes,
    wrapper: "private",
    sidebarOptions: {
      label: "Redeem Codes",
      section: "pages"
    }
  },
  {
    path: "*",
    component: NotFound,
    wrapper: "public"
  }
]
export const sidebarSectionsConfig = {
  main: {},
  pages: {
    label: "Pages"
  }
}
export const sidebarGroupsConfig = {
  group: {
    label: "Group"
  }
}
/*
  Injects types properly, we need both:
  - typed config to achieve stricter type checking inside `getSidebarSections` and `addRoute`
  - untyped config to exctract exact keys for SidebarSectionId and SidebarGroupId
*/
const sectionsConfig = sidebarSectionsConfig as SectionsConfig
const groupsConfig = sidebarGroupsConfig as GroupsConfig

// HELPERS
export const addRoute = (section: (SidebarGroup | SidebarLink)[], route: Route): void => {
  const sidebarOptions = route.sidebarOptions!
  if (sidebarOptions.group) {
    const groupId = sidebarOptions.group
    const group = section.find((item) => (item as SidebarGroup).id === sidebarOptions.group) as SidebarGroup
    if (group) {
      // add route to existing group
      group.children.push({ path: route.path, label: sidebarOptions.label })
    } else {
      // create group and add route as its first child
      section.push({
        id: groupId,
        label: groupsConfig[groupId].label,
        icon: groupsConfig[groupId].icon,
        children: [{ path: route.path, label: sidebarOptions.label }]
      } as SidebarGroup)
    }
  } else {
    // add route as SidebarLink
    section.push({
      path: route.path,
      label: sidebarOptions.label,
      icon: sidebarOptions.icon
    })
  }
}
const getSidebarSections = (): SidebarSection[] => {
  const mapping = routes.reduce((sections, route) => {
    if (route.sidebarOptions) {
      const sectionId = route.sidebarOptions.section
      if (sections[sectionId]) {
        addRoute(sections[sectionId], route)
      } else {
        sections[sectionId] = []
        addRoute(sections[sectionId], route)
      }
    }
    return sections
  }, {} as { [key in SidebarSectionId]: (SidebarGroup | SidebarLink)[] })

  return Object.keys(mapping).map((key: string) => {
    const k = key as SidebarSectionId
    return {
      label: sectionsConfig[k].label,
      items: mapping[k]
    }
  })
}

export const sidebarSections = getSidebarSections()
